// src/lib/partner/partnerTeam.js
import { supabase } from "../../supabaseClient";

const VALID_ROLES = [
  "owner",
  "admin",
  "cohort_manager",
  "facilitator",
  "viewer",
];

const VALID_STATUSES = [
  "active",
  "inactive",
  "suspended",
];

function normaliseEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normaliseRole(value) {
  const role = String(value || "")
    .trim()
    .toLowerCase();

  if (!VALID_ROLES.includes(role)) {
    throw new Error(
      "Please select a valid partner team role."
    );
  }

  return role;
}

function normaliseStatus(value) {
  const status = String(value || "")
    .trim()
    .toLowerCase();

  if (!VALID_STATUSES.includes(status)) {
    throw new Error(
      "Please select a valid partner team status."
    );
  }

  return status;
}

function normaliseAssignedCohorts(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed)
        ? parsed
        : [];
    } catch {
      return [];
    }
  }

  return [];
}

function normaliseTeamMember(member) {
  return {
    ...member,

    assigned_cohorts:
      normaliseAssignedCohorts(
        member?.assigned_cohorts
      ),

    assigned_cohort_count:
      Number(
        member?.assigned_cohort_count ||
          0
      ),

    is_current_user:
      Boolean(
        member?.is_current_user
      ),

    can_manage:
      Boolean(
        member?.can_manage
      ),
  };
}

function normaliseResponsibility(row) {
  return {
    ...row,
    assigned: Boolean(row?.assigned),
    is_programme_manager: Boolean(
      row?.is_programme_manager
    ),
    is_facilitator: Boolean(
      row?.is_facilitator
    ),
  };
}

export async function fetchPartnerTeam(
  partnerId = null
) {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_partner_team",
    {
      p_partner_id:
        partnerId || null,
    }
  );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data.map(
        normaliseTeamMember
      )
    : [];
}

export async function addExistingPartnerTeamMember({
  partnerId,
  email,
  role,
}) {
  const cleanEmail =
    normaliseEmail(email);

  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }

  if (
    !cleanEmail ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      cleanEmail
    )
  ) {
    throw new Error(
      "Please enter a valid team member email address."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "add_existing_partner_team_member",
    {
      p_partner_id:
        partnerId,

      p_email:
        cleanEmail,

      p_role:
        normaliseRole(
          role
        ),
    }
  );

  if (error) {
    throw error;
  }

  return data;
}

export async function updatePartnerTeamMember({
  partnerMemberId,
  role,
  status,
}) {
  if (!partnerMemberId) {
    throw new Error(
      "The partner team member could not be identified."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "update_partner_team_member",
    {
      p_partner_member_id:
        partnerMemberId,

      p_role:
        normaliseRole(
          role
        ),

      p_status:
        normaliseStatus(
          status
        ),
    }
  );

  if (error) {
    throw error;
  }

  return data;
}

// Legacy assigned/not-assigned helper kept for compatibility with any
// screens that have not yet moved to explicit cohort responsibilities.
export async function fetchPartnerMemberCohortAssignments(
  partnerMemberId
) {
  if (!partnerMemberId) {
    throw new Error(
      "The partner team member could not be identified."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_member_cohort_assignments",
    {
      p_partner_member_id:
        partnerMemberId,
    }
  );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data.map(
        (row) => ({
          ...row,
          assigned:
            Boolean(
              row.assigned
            ),
        })
      )
    : [];
}

// Legacy setter retained for backwards compatibility. New Partner staff UI
// should use setPartnerMemberCohortResponsibilities instead.
export async function setPartnerMemberCohortAssignments({
  partnerMemberId,
  cohortIds,
}) {
  if (!partnerMemberId) {
    throw new Error(
      "The partner team member could not be identified."
    );
  }

  const uniqueCohortIds = [
    ...new Set(
      (
        Array.isArray(
          cohortIds
        )
          ? cohortIds
          : []
      ).filter(Boolean)
    ),
  ];

  const {
    data,
    error,
  } = await supabase.rpc(
    "set_partner_member_cohort_assignments",
    {
      p_partner_member_id:
        partnerMemberId,

      p_cohort_ids:
        uniqueCohortIds,
    }
  );

  if (error) {
    throw error;
  }

  return Number(
    data || 0
  );
}

export async function fetchPartnerMemberCohortResponsibilities(
  partnerMemberId
) {
  if (!partnerMemberId) {
    throw new Error(
      "The partner team member could not be identified."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_member_cohort_responsibilities",
    {
      p_partner_member_id:
        partnerMemberId,
    }
  );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data.map(
        normaliseResponsibility
      )
    : [];
}

export async function setPartnerMemberCohortResponsibilities({
  partnerMemberId,
  cohortId,
  isProgrammeManager = false,
  isFacilitator = false,
}) {
  if (!partnerMemberId) {
    throw new Error(
      "The partner team member could not be identified."
    );
  }

  if (!cohortId) {
    throw new Error(
      "The cohort could not be identified."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "set_partner_member_cohort_responsibilities",
    {
      p_partner_member_id:
        partnerMemberId,
      p_cohort_id:
        cohortId,
      p_is_programme_manager:
        Boolean(
          isProgrammeManager
        ),
      p_is_facilitator:
        Boolean(
          isFacilitator
        ),
    }
  );

  if (error) {
    throw error;
  }

  return data || null;
}
